import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';

import { CoprocultivoSumaChip } from '@/features/analisis/coprocultivo/components/CoprocultivoSumaChip';

import type { CoprocultivoComplementarioState, PosicionHpgValida } from './hpgDialogTypes';

type PatchFields = Partial<
  Pick<
    CoprocultivoComplementarioState,
    | 'numerosHpgReferencia'
    | 'porcentajeOstertagia'
    | 'porcentajeTrichostrongylus'
    | 'porcentajeCooperia'
    | 'porcentajeHaemonchus'
    | 'porcentajeOesophagostomum'
    | 'porcentajeNematodirus'
    | 'observacion'
    | 'sumError'
  >
>;

interface HpgDialogCoprocultivoTabProps {
  state: CoprocultivoComplementarioState;
  posicionesHpgValidas: PosicionHpgValida[];
  onChange: (patch: PatchFields) => void;
  onEliminarTab: () => void;
}

const GENEROS: { key: keyof PatchFields & `porcentaje${string}`; label: string }[] = [
  { key: 'porcentajeOstertagia', label: 'Ostertagia' },
  { key: 'porcentajeTrichostrongylus', label: 'Trichostrongylus' },
  { key: 'porcentajeCooperia', label: 'Cooperia' },
  { key: 'porcentajeHaemonchus', label: 'Haemonchus' },
  { key: 'porcentajeOesophagostomum', label: 'Oesophagostomum' },
  { key: 'porcentajeNematodirus', label: 'Nematodirus' },
];

export function HpgDialogCoprocultivoTab({
  state,
  posicionesHpgValidas,
  onChange,
  onEliminarTab,
}: HpgDialogCoprocultivoTabProps): JSX.Element {
  const selectedPosiciones = posicionesHpgValidas.filter((p) =>
    state.numerosHpgReferencia.includes(p.posicion),
  );

  const promedioHpg = useMemo(() => {
    const valores = state.numerosHpgReferencia
      .map((n) => posicionesHpgValidas.find((p) => p.posicion === n)?.valor)
      .filter((v): v is number => v !== null && v !== undefined);
    if (valores.length === 0) return null;
    return Math.round(valores.reduce((s, v) => s + v, 0) / valores.length);
  }, [state.numerosHpgReferencia, posicionesHpgValidas]);

  function onPct(key: keyof PatchFields & `porcentaje${string}`, raw: string): void {
    const val = raw === '' ? null : Number(raw);
    onChange({ [key]: val, sumError: false });
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Tooltip title="Eliminar este Coprocultivo">
          <IconButton size="small" color="error" onClick={onEliminarTab}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* HPG muestra selector */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Autocomplete
          multiple
          size="small"
          sx={{ flex: 1 }}
          options={posicionesHpgValidas}
          getOptionLabel={(opt) =>
            `Muestra ${opt.posicion} (${opt.valor !== null ? `${opt.valor} hpg` : '—'})`
          }
          isOptionEqualToValue={(opt, val) => opt.posicion === val.posicion}
          value={selectedPosiciones}
          onChange={(_, newValue) => {
            onChange({ numerosHpgReferencia: newValue.map((p) => p.posicion) });
          }}
          renderInput={(params) => (
            <TextField {...params} label="Muestras HPG vinculadas" />
          )}
          renderTags={(value, getTagProps) =>
            value.map((opt, index) => (
              <Chip
                {...getTagProps({ index })}
                key={opt.posicion}
                label={`Muestra ${opt.posicion}`}
                size="small"
              />
            ))
          }
          noOptionsText="No hay muestras HPG válidas"
        />
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {promedioHpg !== null ? `Promedio HPG: ${promedioHpg}` : 'Sin pool'}
        </Typography>
      </Stack>

      {/* Porcentajes */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle2">Porcentajes por género</Typography>
        <CoprocultivoSumaChip
          ostertagia={state.porcentajeOstertagia}
          trichostrongylus={state.porcentajeTrichostrongylus}
          cooperia={state.porcentajeCooperia}
          haemonchus={state.porcentajeHaemonchus}
          oesophagostomum={state.porcentajeOesophagostomum}
          nematodirus={state.porcentajeNematodirus}
        />
      </Box>

      {state.sumError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Corregí los porcentajes para que sumen 100 o 0.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 2,
          mb: 1.5,
        }}
      >
        {GENEROS.map(({ key, label }) => (
          <TextField
            key={key}
            size="small"
            fullWidth
            type="number"
            label={label}
            InputLabelProps={{ sx: { fontStyle: 'italic' } }}
            inputProps={{ min: 0, max: 100, step: 1 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            value={state[key] !== null && state[key] !== undefined ? String(state[key]) : ''}
            onChange={(e) => onPct(key, e.target.value)}
          />
        ))}
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontStyle: 'italic', display: 'block', mb: 2 }}
      >
        La suma debe ser 100 (parasitismo identificado) o 0 (resultado negativo).
      </Typography>

      <TextField
        size="small"
        fullWidth
        multiline
        rows={2}
        label="Observación"
        value={state.observacion ?? ''}
        onChange={(e) => onChange({ observacion: e.target.value || null })}
      />
    </Box>
  );
}
